import moment from 'moment';
import publicConfig from '../../../public-config';
import addOnsData from './catering-order-flow/data/catering-add-ons.json';

export const getSelectedMenuItems = (
  itemFormValues,
  selectedCateringPackage,
  totalGuests
) => {
  const selectedMenuItems = Object.entries(itemFormValues || {}).reduce(
    (acc, [menuSectionId, formValue]) => {
      const menuSection = selectedCateringPackage.menu.menu_sections.find(
        ({ id }) => id === menuSectionId
      );

      if (typeof formValue === 'string') {
        // this is a radio button selection
        const menuItem = menuSection?.items?.find(
          ({ name }) => name === formValue
        );
        let total = menuItem?.price?.value || 0;
        if (menuItem?.price?.per_person && totalGuests > 1) {
          total *= totalGuests;
        }
        acc.push({
          ...menuItem,
          total
        });
      }

      if (typeof formValue === 'object') {
        // this is a checkbox selection

        const menuItemNames = Object.keys(formValue);
        menuItemNames.forEach((menuItemName) => {
          if (!formValue[menuItemName]?.isSelected) {
            return;
          }

          const menuItem = menuSection?.items.find(
            ({ name }) => name === menuItemName
          );
          const selectedSizeFormValue = formValue[menuItemName]?.selectedSize;
          const selectedSize = menuItem?.sizes?.find(
            ({ name }) => name === selectedSizeFormValue
          );
          let total = selectedSize?.price?.value || menuItem?.price?.value || 0;
          if (
            (selectedSize?.price?.per_person || menuItem?.price?.per_person) &&
            totalGuests > 1
          ) {
            total *= totalGuests;
          }
          acc.push({
            ...menuItem,
            selectedSize,
            total
          });
        });
      }

      return acc;
    },
    []
  );
  return selectedMenuItems;
};

export const getSelectedAddOns = (addOnsFormValues, totalGuests) => {
  const selectedAddOns = [];
  Object.entries(addOnsFormValues || {}).forEach(([addOnId, value]) => {
    const addOnData = addOnsData.find((addOn) => addOn.id === addOnId);

    if (addOnData.id === 'bartending' && value) {
      const { bartendingTotal, rate } = getBartendingQuote(
        totalGuests,
        addOnData
      );
      selectedAddOns.push({
        name: `${totalGuests} guests`,
        title: `${addOnData.title} (${totalGuests} guests)`,
        total: bartendingTotal,
        price: { value: bartendingTotal },
        type: addOnData.id
      });
    } else if (typeof value === 'string') {
      // this is a radio button selection
      const selectedOption = addOnData.options.find(
        (option) => option.name === value
      );

      if (selectedOption) {
        let title = `${addOnData.title} (${selectedOption.name})`;
        let price = selectedOption.price.value || 0;
        if (selectedOption.price.per_person) {
          price *= totalGuests;
        }
        if (selectedOption) {
          selectedAddOns.push({
            ...selectedOption,
            total: price,
            title,
            type: addOnData.id
          });
        }
      }
    } else if (typeof value === 'object') {
      // this is from a counter input
      Object.entries(value || {}).forEach(([optionId, count]) => {
        if (count < 1) {
          return;
        }

        const selectedOption = addOnData.counters.find(
          (option) => option.name === optionId
        );
        if (selectedOption) {
          let title = `${addOnData.title} (${selectedOption.name})`;
          if (addOnData.id === 'coffee') {
            title = 'Coffee (20 servings)';
          }

          if (addOnData.id === 'drinks') {
            title = selectedOption.name;
          }

          selectedAddOns.push({
            ...selectedOption,
            total: selectedOption.price.value * count,
            count,
            title,
            type: addOnData.id
          });
        }
      });
    }
  });

  return selectedAddOns;
};

export const calculateOrderTotal = (
  selectedPackage,
  selectedItems,
  selectedAddOns,
  totalGuests
) => {
  let total = 0;

  let packagePrice = 0;
  if (selectedPackage?.price?.value && !selectedPackage?.price?.starts_at) {
    packagePrice = parseFloat(selectedPackage.price.value);
  }
  if (selectedPackage?.price?.per_person && totalGuests > 1) {
    packagePrice *= totalGuests;
  }

  const itemSelectionsTotal = (selectedItems || []).reduce(
    (acc, { price, selectedSize }) => {
      price = selectedSize?.price || price;
      let itemTotal = parseFloat(price?.value || 0);
      if (price?.per_person && totalGuests > 1) {
        itemTotal *= totalGuests;
      }
      return acc + itemTotal;
    },
    0
  );

  const addOnsTotal = (selectedAddOns || []).reduce((acc, { total }) => {
    return acc + total;
  }, 0);

  total = packagePrice + itemSelectionsTotal + addOnsTotal;

  return total.toFixed(2);
};

const formatOrderDate = (date) => {
  return;
};

export const createOrderPayload = (
  formValues,
  selectedPackage,
  booking,
  profile
) => {
  const { selectedItems, addOns, deliveryTime, cateringNotes } =
    formValues || {};

  const { start } = booking || {};
  const { date } = start || {};

  const selectedAddOns = getSelectedAddOns(addOns, booking?.number_of_guests);
  const payloadAddOns = (selectedAddOns || []).reduce((addOns, currAddOn) => {
    let addOnObj = addOns.find((a) => a.type === currAddOn.type);
    const addOnOption = {
      name: currAddOn.name,
      price: currAddOn.price.value.toFixed(2),
      count:
        currAddOn.count ||
        (currAddOn.price.per_person ? booking?.number_of_guests : 1)
    };
    if (!addOnObj) {
      addOnObj = {
        type: currAddOn.type,
        options: [addOnOption]
      };
      addOns.push(addOnObj);
    } else {
      addOnObj.options.push(addOnOption);
    }
    return addOns;
  }, []);

  const selectedMenuItems = getSelectedMenuItems(
    selectedItems,
    selectedPackage,
    booking?.number_of_guests
  );
  const payloadMenuItems = (selectedMenuItems || []).map((item) => {
    return {
      name: item.name,
      count:
        !item?.price?.value || item?.price?.per_person
          ? booking?.number_of_guests
          : 1,
      price: (item?.selectedSize || item).price.value.toFixed(2),
      size: item?.selectedSize?.name
    };
  });

  const orderTotal = calculateOrderTotal(
    selectedPackage,
    selectedMenuItems,
    selectedAddOns,
    booking?.number_of_guests
  );

  const orderDate = moment({
    year: date.y,
    month: date.m - 1,
    day: date.d
  }).format('YYYY-MM-DD');

  // location and zip are appended to payload in contracts route
  const orderPayload = {
    referenceId: booking._id,
    date: orderDate,
    time: deliveryTime,
    group_size: booking.number_of_guests,
    selected_package: JSON.stringify({
      name: selectedPackage.name,
      price:
        selectedPackage.price?.value && !selectedPackage.price?.starts_at
          ? selectedPackage.price.value.toFixed(2)
          : 0,
      menu_items: payloadMenuItems
    }),
    total_amount: orderTotal,
    name: `${profile?.first_name} ${profile?.last_name}`,
    email: profile?.email,
    phone: profile?.phone,
    notes: cateringNotes
  };

  if (payloadAddOns.length) {
    orderPayload.add_ons = JSON.stringify(payloadAddOns);
  }

  return orderPayload;
};

export const fetchRedirectUrl = async (
  accessToken,
  bookingId,
  orderPayload
) => {
  const res = await fetch(`/v1/contracts/${bookingId}/catering/create`, {
    host: publicConfig.external_address,
    method: 'POST',
    body: JSON.stringify(orderPayload),
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    }
  });
  const resData = await res.json();
  return resData;
};

export const getBartendingQuote = (numberOfGuests, bartendingAddOn) => {
  const { tiers } = bartendingAddOn;

  const priceTier =
    numberOfGuests < 50 ? tiers[0] : numberOfGuests < 60 ? tiers[1] : tiers[2];

  const rate = priceTier.price.value;
  const bartendingTotal = rate * numberOfGuests;

  return { bartendingTotal, rate };
};

export const checkCateringEligibility = (
  listingMetro,
  totalGuests,
  bookingStart
) => {
  const { date, time } = bookingStart || {};

  const bookingStartTimestamp = new Date(
    date?.y,
    date?.m - 1,
    date?.d,
    time?.h,
    time?.m
  ).getTime();

  const eventStart = moment(bookingStartTimestamp);
  const now = moment();

  const eligibleMetros = ['LAMetro'];

  const isEligible = !!(
    eligibleMetros.includes(listingMetro) &&
    totalGuests >= 6 &&
    eventStart.diff(now, 'hours') > 48
  );

  return isEligible;
};
